<template>
    <search-layout
        v-if="searchResponse"
        :map-expanded="mapExpanded"
        :tabs="mobileTabs"
        :search-active="searchActive"
        :offers-count="filteredOffers.length"
    >
        <template v-slot:breadcrumbs>
            <flight-breadcrumbs :search-request="searchRequest" />
        </template>
        <template v-slot:mobileTabs>
            <package-mobile-search-page-tab v-if="isMobile" ref="mobileTabs" v-model="mobileTabs" />
        </template>
        <template v-if="offers.length" v-slot:preinfo>
            <v-col cols="12">
                <selected-schedule-offers :active-route="routeIndex" @change-route="changeRoute" />
            </v-col>
        </template>
        <template v-slot:left-sidebar>
            <flight-search-summary :search-request="searchRequest" :search-active="searchActive" />
            <sort-select v-model="sortKey" :items="sortItems" />
            <flight-filters
                :filter-values="filterValues"
                :filters="searchResponse.filters"
                :filtered-offers-count="filteredOffers.length"
                :currency="currency"
                @change="changeFilter"
                @reset="resetFilters"
            />
        </template>
        <template v-slot:main-content>
            <search-progress
                :filtered-offers-count="filteredOffers.length"
                :total-offers-count="searchResponse.offers.length"
                :search-active="searchActive"
                hide-stop-btn
            />
            <flight-offers :offers="offers">
                <template v-slot:price="{offer}">
                    <schedule-flight-selector :offer="offer" :index="routeIndex" @select-offer="selectOffer" />
                </template>
            </flight-offers>
            <pagination v-model="page" :pages="pages" />
            <conditions-modal>
                <template v-slot:info>
                    <flight-conditions-modal-info />
                </template>
            </conditions-modal>
        </template>
        <template v-slot:right-sidebar>
            <client-only>
                <flight-map v-model="mapExpanded" :points="mapPoints" />
            </client-only>
        </template>
    </search-layout>
</template>

<script lang="ts">
    import {Component, mixins, Watch} from 'vue-facing-decorator'
    import MobileSearchPageTabs from '@/components/snippets/MobileSearchPageTabs.vue'
    import Pagination from '@/components/search/Pagination.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import FlightBreadcrumbs from '@/components/flights/FlightBreadcrumbs.vue'
    import FlightSearchSummary from '@/components/flights/search/FlightSearchSummary.vue'
    import SearchProgress from '~src/components/search/searchProgress.src'
    import FlightMap from '@/components/flights/search/FlightMap.vue'
    import FlightOffers from '@/components/flights/search/offers/FlightOffers.vue'
    import FlightBestOffers from '@/components/parts/Flights/FlightBestOffers.vue'
    import {SearchPageBase} from '@/mixins/SearchPageBase.vue'
    import {searchRequest} from '~src/utils/flights/flights-blank-states.src'
    import {FlightStoreMixin} from '@/mixins/FlightStoreMixin.vue'
    import SearchLayout from '@/components/parts/SearchLayout.vue'
    import FlightFilters from '~src/components/flights/search/flightFilters.src'
    import SelectedScheduleOffers from '@/components/flights/search/schedule/SelectedScheduleOffers.vue'
    import ScheduleFlightSelector from '@/components/flights/search/schedule/ScheduleFlightSelector.vue'
    import {FlightPageMixin} from '@/components/flights/mixins/FlightPageMixin.vue'
    import {isEqual} from '@/utils/helpers'
    import SortSelect from '@/components/search/SortSelect.vue'
    import FlightConditionsModalInfo from '@/components/flights/snippets/FlightConditionsModalInfo.vue'
    import {useFlightRuntimeData} from '~/composables/flight'
    import type {RouteLocationNormalized} from 'vue-router'

    @Component({
        components: {
            FlightConditionsModalInfo,
            SortSelect,
            FlightSearchSummary,
            SearchProgress,
            FlightMap,
            FlightOffers,
            Pagination,
            MobileSearchPageTabs,
            FlightBreadcrumbs,
            ConditionsModal,
            SearchLayout,
            FlightBestOffers,
            FlightFilters,
            SelectedScheduleOffers,
            ScheduleFlightSelector,
            PackageMobileSearchPageTab: defineAsyncComponent(
                () => import('~/components/packages/search/PackageMobileSearchPageTab.vue')
            ),
        },
        async setup() {
            const route = useRoute()
            const name = Array.isArray(route.query.routes) ? route.query.routes.join('&') : route.query.routes
            await useAsyncData(`flights-schedule-${name}`, async () => {
                await useFlightRuntimeData(route)
                return true
            })
        },
    })
    export class FlightsSchedulePage extends mixins(FlightPageMixin, SearchPageBase, FlightStoreMixin) {
        routeIndex = 0
        mobileTabs = 1

        @Watch('$route')
        onQueryChange(route: RouteLocationNormalized) {
            if (route.name !== 'flightsSchedule') return
            this.search()
        }

        @Watch('routeIndex')
        async onRouteIndexChange(val) {
            this.productRuntimeStore.SET_ACTIVE_ROUTE(val)
            await this.$nextTick()
            this.productRuntimeStore.filter({offers: this.searchResponse.offers, filters: this.filterValues})
        }

        async fetch() {
            await this.loadRuntimeData()
        }

        async mounted() {
            await this.$store.restored
            if (
                this.sortKey === 'priceAsc' ||
                this.sortKey === 'priceDesc' ||
                this.sortKey === 'priceDeltaAsc' ||
                this.sortKey === 'priceDeltaDesc'
            ) {
                this.productStore.SET_SORT('best')
            }
            if (
                !isEqual(this.productStore.scheduleSearchRequest, this.searchRequest) ||
                !this.productStore.hasScheduleOffers
            ) {
                await this.search()
            } else {
                this.resetPageLock = true
                this.productRuntimeStore.filter({offers: this.searchResponse.offers, filters: this.filterValues})
            }
        }

        async search() {
            await this.productRuntimeStore.scheduleSearch(this.searchRequest)
        }

        get searchRequest() {
            //TODO Need filter $route.query params
            const rq = Object.assign(searchRequest(), this.$route.query)
            if (typeof rq.routes === 'string') {
                rq.routes = [rq.routes]
            }
            if (typeof rq.childrenAges === 'string') {
                rq.childrenAges = [parseInt(rq.childrenAges, 10)]
            }
            rq.convertToCurrency = persistentStore.currency
            rq.adults = parseInt(rq.adults, 10)
            rq.partialResponse = false
            if (rq.citizenshipId) {
                rq.citizenshipId = parseInt(rq.citizenshipId, 10)
            }
            return rq
        }

        beforeUnmount() {
            this.productRuntimeStore.SET_OFFERS([])
        }

        selectOffer() {
            if (this.routeIndex < this.routes.length - 1) {
                this.routeIndex++
            } else {
                this.mobileTabs = 3
            }
        }

        changeRoute(route) {
            this.routeIndex = route
            this.mobileTabs = 1
        }

        changeFilter(data) {
            Object.assign(data, {routeIndex: this.routeIndex})
            this.productStore.SET_SCHEDULE_FILTER(data)
            this.productRuntimeStore.filter({offers: this.searchResponse.offers, filters: this.filterValues})
        }

        resetFilters() {
            this.productStore.RESET_SCHEDULE_FILTERS(this.routeIndex)
            this.productRuntimeStore.filter({offers: this.searchResponse.offers, filters: this.filterValues})
        }

        get mapPoints() {
            let result = []
            this.productRuntimeStore.routes.forEach(({arrival, departure}) => {
                result = [...result, departure, arrival]
            })
            return result
        }

        get routes() {
            return this.productRuntimeStore.routes
        }

        get sortKey() {
            return this.productStore.sortFnName
        }

        set sortKey(sortKey) {
            this.productStore.SET_SORT(sortKey)
            this.productRuntimeStore.filter({offers: this.searchResponse.offers, filters: this.filterValues})
        }

        get searchResponse() {
            return this.productStore.searchScheduleResponse.routes[this.routeIndex]
        }

        get filterValues() {
            return this.productStore.scheduleFilters[this.routeIndex]
        }

        get filteredOffers() {
            return this.productRuntimeStore.offers
        }

        get searchActive() {
            return this.productRuntimeStore.scheduleSearchActive
        }

        get sortItems() {
            return [
                {
                    text: this.$t('sort.best'),
                    value: 'best',
                },
                {
                    text: this.$t('sort.duration_desc'),
                    value: 'durationDesc',
                },
                {
                    text: this.$t('sort.duration_asc'),
                    value: 'durationAsc',
                },
                {
                    text: this.$t('sort.airline'),
                    value: 'airline',
                },
                {
                    text: this.$t('sort.departure_time'),
                    value: 'departureTime',
                },
                {
                    text: this.$t('sort.arrival_time'),
                    value: 'arrivalTime',
                },
            ]
        }

        get currency() {
            return this.productStore.scheduleSearchRequest.convertToCurrency !== 'ORIGINAL'
                ? this.productStore.searchRequest.convertToCurrency
                : this.filteredOffers[0]?.price.currency
        }
    }

    export default FlightsSchedulePage
</script>
